import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { loadStripe } from '@stripe/stripe-js';

import { Spinner } from '../Loaders';
import {
  getPaymentPublishableKey,
  getPublicPaymentLinkPublishableKey,
} from '../../pages/ProtectedPages/Dashboard/actions';

const stripePromiseCache = new Map();

const getStripePromise = (publishableKey) => {
  if (!publishableKey) return null;
  if (!stripePromiseCache.has(publishableKey)) {
    stripePromiseCache.set(publishableKey, loadStripe(publishableKey));
  }
  return stripePromiseCache.get(publishableKey);
};

export const StripeElementsProvider = ({
  mode = 'customer',
  paymentToken = '',
  enabled = true,
  children,
}) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['paymentPublishableKey', mode, paymentToken],
    queryFn: () =>
      mode === 'public'
        ? getPublicPaymentLinkPublishableKey({ pt: paymentToken })
        : getPaymentPublishableKey(),
    select: (res) => res?.data?.data,
    enabled: enabled && (mode === 'customer' || Boolean(paymentToken)),
    retry: false,
  });

  const stripePromise = useMemo(() => {
    if (data?.payment_platform !== 'stripe' || !data?.payment_publishable_key) {
      return null;
    }
    // console.log('data.payment_publishable_key', data.payment_publishable_key);
    return getStripePromise(data.payment_publishable_key);
  }, [data?.payment_platform, data?.payment_publishable_key]);

  if (!enabled) {
    return children;
  }

  if (isLoading) {
    return (
      <div className="d--flex justify-content--center align-items--center p--md w--full">
        <Spinner size="md" />
      </div>
    );
  }

  if (isError || !stripePromise) {
    const message =
      error?.response?.data?.data?.[0]?.msg ||
      (data?.payment_platform && data.payment_platform !== 'stripe'
        ? `Payment platform "${data.payment_platform}" is not supported.`
        : 'Payment is not configured. Please contact support.');

    return <div className="text--danger font--sm">{message}</div>;
  }

  return (
    <>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { stripePromise });
        }
        return child;
      })}
    </>
  );
};
